import type { RawVideoData } from '@lumen5/luminary';

export function getTimeStringFromSeconds(total_seconds: number, loading = false): string {
    if (loading) {
        return 'XX:XX';
    }
    let seconds = Math.round(total_seconds % 60);
    let minutes = Math.floor(total_seconds / 60);

    if (isNaN(seconds) || isNaN(minutes)) {
        return 'XX:XX';
    }

    // ensure both are at least 0
    seconds = Math.max(seconds, 0);
    minutes = Math.max(minutes, 0);

    if (seconds === 60) {
        seconds = 0;
        minutes += 1;
    }

    const seconds_string = seconds <= 9 ? '0' + seconds.toString() : seconds.toString();
    const minutes_string = minutes <= 9 ? '0' + minutes.toString() : minutes.toString();
    return minutes_string + ':' + seconds_string;
}

export function getTimeFromXPosition(
    xPosition: number,
    containerWidth: number,
    totalSeconds: number,
): number {
    let time = 0;
    if (xPosition > 0 && containerWidth > xPosition) {
        time = (xPosition / containerWidth) * totalSeconds;
    }
    return time;
}

/*
    Chrome has a bug where the MediaRecorder produces blobs that don't have their metadata
    (like duration) nicely located in the file. To get around that, we do a weird thing
    where we set the currentTime of the video randomly until the duration metadata is properly
    found.
    Doing this means that the <video/> tag will properly show progress in the scrubber bar.
    
    Works for both video and audio
 
    Chrome bug: https://bugs.chromium.org/p/chromium/issues/detail?id=642012
    Source code credit: https://github.com/evictor/get-blob-duration/blob/master/src/getBlobDuration.js
*/
export async function validateInfinityDuration(video: HTMLVideoElement) {
    while (video.duration === Infinity || isNaN(video.duration)) {
        await new Promise<void>((resolve) => {
            video.ontimeupdate = () => {
                video.ontimeupdate = null;
                resolve();
            };
            video.currentTime = 10000000 * Math.random();
        });
    }
}

export const getApproximateTimeFromSeconds = (total_seconds: number): string => {
    // round to nearest minute, or to neareset 10 seconds if less than a minute
    if (total_seconds < 10) {
        return '~10s';
    }
    if (total_seconds < 60) {
        return `~${Math.round(total_seconds / 10) * 10}s`;
    }
    return `~${Math.round(total_seconds / 60)} min`;
};

export const getNaturalDurationFromSeconds = (total_seconds: number): string => {
    const seconds = Math.round(total_seconds % 60);
    const minutes = Math.floor(total_seconds / 60);

    if (minutes === 0) {
        return `${seconds}s`;
    }

    if (seconds === 0) {
        return `${minutes}m`;
    }

    return `${minutes}m${seconds}s`;
};

export const getCompactDurationFromMs = (durationMs: number): string => {
    const totalSeconds = Math.round(durationMs / 1000);
    if (totalSeconds < 60) {
        return `${totalSeconds}s`;
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (seconds === 0) {
        return `${minutes}m`;
    }
    return `${minutes}m ${seconds}s`;
};

export const getDurationFromRawVideoData = (videoData: RawVideoData): number => {
    const allSubslides = videoData?.slides?.reduce<Record<string, any>[]>((acc, slide) => {
        return [...acc, ...slide.subslides];
    }, []);
    return (
        allSubslides.reduce((acc, subslide) => {
            return acc + subslide.duration;
        }, 0) / 1000
    );
};
